import React from 'react';

import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import SignatureIcon from 'icons/ic-signature-24.svg';
import InfoIcon from 'icons/info.svg';
import { Trans, useTranslation } from 'react-i18next';

import { useLatestVersionQuery } from 'components/hooks/latestVersionQueries';
import { useProjectPersonnelsQuery } from 'components/hooks/projectPersonnelQueries';
import { useCreateProjectsReviewsBulkCreationMutation } from 'components/hooks/projectsReviewsBulkCreationMutations';
import { useCreateProjectsReviewsBulkReminderMutation } from 'components/hooks/projectsReviewsBulkReminderMutations';
import { useProjectsSignatureStatusQuery } from 'components/hooks/projectsSignatureStatusQueries';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import SuccessToast from 'components/application/SuccessToast';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

import { asFormattedDateTime } from 'components/helpers/dates';

export default function ProjectPersonnelDigitalSignaturesInfo() {
  const { addInedibleToast, addToast, handleBurnToast } = useBreadBoard();

  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectPersonnel.components.ProjectPersonnelDigitalSignaturesInfo',
  });

  const { t: i18nTProjects } = useTranslation('common', {
    keyPrefix: 'projects',
  });

  const { projectId } = useProjectPersonnelContext();

  const createProjectsReviewsBulkReminderMutation =
    useCreateProjectsReviewsBulkReminderMutation({ projectId });

  const createProjectsReviewsBulkCreationMutation =
    useCreateProjectsReviewsBulkCreationMutation({ projectId });

  const {
    data: domainLatestVersion,
    isFetching: isDomainLatestVersionFetching,
    isSuccess: isDomainLatestVersionSuccess,
  } = useLatestVersionQuery({ projectId });

  const {
    data: domainSignatureStatus,
    isFetching: isDomainSignatureStatusFetching,
    isSuccess: isDomainSignatureStatusSuccess,
  } = useProjectsSignatureStatusQuery({ projectId });

  const {
    data: domainProjectPersonnel,
    isSuccess: isDomainProjectPersonnelSuccess,
  } = useProjectPersonnelsQuery({ params: { meta_only: true }, projectId });

  if (
    !(
      isDomainLatestVersionSuccess &&
      isDomainProjectPersonnelSuccess &&
      isDomainSignatureStatusSuccess
    )
  ) {
    return <Skeleton />;
  }

  const handleRequestSignaturesOptionClick = () => {
    createProjectsReviewsBulkCreationMutation.mutate(
      {},
      {
        onSuccess: () => {
          addToast(
            <SuccessToast
              message={
                <Trans
                  i18nKey='projectPersonnel.components.ProjectPersonnelDigitalSignaturesInfo.handleRequestSignaturesOptionClick.success'
                  ns='components'
                  values={{ versionNumber: latestVersionNumber }}
                />
              }
              onBurnToast={handleBurnToast}
            />,
          );
        },
        onError: () => {
          addInedibleToast({
            fullMessage: (
              <Trans
                i18nKey='projectPersonnel.components.ProjectPersonnelDigitalSignaturesInfo.handleRequestSignaturesOptionClick.error'
                ns='components'
                values={{ versionNumber: latestVersionNumber }}
              />
            ),
          });
        },
      },
    );
  };

  const handleSendRemindersOptionClick = () => {
    createProjectsReviewsBulkReminderMutation.mutate(
      { projectId },
      {
        onSuccess: (data) => {
          addToast(
            <SuccessToast
              message={
                <Trans
                  components={{ span: <span className='tw-font-medium' /> }}
                  i18nKey='projectPersonnel.components.ProjectPersonnelDigitalSignaturesInfo.handleSendRemindersOptionClick.success'
                  ns='components'
                  values={{
                    count: data.data.relationships.reviews.data.length,
                  }}
                />
              }
              onBurnToast={handleBurnToast}
            />,
          );
        },
        onError: () => {
          addInedibleToast({
            fullMessage: i18nT('handleSendRemindersOptionClick.error'),
          });
        },
      },
    );
  };

  const {
    data: {
      attributes: { displayNumber: latestVersionNumber },
      id: latestVersionId,
    },
  } = domainLatestVersion;

  const {
    data: {
      attributes: { isEmailable, isRemindable },
      relationships: {
        requestedVersion: { data: requestedVersionData },
        signedVersion: { data: signedVersionData },
      },
    },
  } = domainSignatureStatus;

  const requestedVersion =
    requestedVersionData ?
      domainSignatureStatus.included.version[requestedVersionData.id]
    : null;

  const signedVersion =
    signedVersionData ?
      domainSignatureStatus.included.version[signedVersionData.id]
    : null;

  const signedOrRequestedVersion = signedVersion || requestedVersion;

  const hasNoPersonnel = domainProjectPersonnel.meta.scopedCount === 0;

  const isReminderOptionDisabled =
    !isRemindable || createProjectsReviewsBulkReminderMutation.isLoading;

  const isSignOptionDisabled =
    latestVersionNumber === requestedVersion?.attributes.displayNumber ||
    createProjectsReviewsBulkCreationMutation.isLoading ||
    isDomainLatestVersionFetching ||
    isDomainSignatureStatusFetching;

  return (
    <div className='tw-flex tw-h-16 tw-items-center tw-justify-between tw-rounded-lg tw-border-0 tw-bg-amber-025 tw-px-5 tw-text-amber-800 [&_svg_path]:tw-fill-amber-800'>
      <div className='tw-flex tw-items-center'>
        <InfoIcon className='tw-mr-2' height={20} width={20} />
        <span>
          {hasNoPersonnel ?
            i18nTProjects('noPersonnelAssigned', {
              interpolation: { escapeValue: false },
              singularRAMSName,
            })
          : requestedVersion ?
            isEmailable ?
              signedVersion ?
                <Trans
                  i18nKey={`projectPersonnel.components.ProjectPersonnelDigitalSignaturesInfo.signed${
                    (
                      latestVersionNumber >
                      signedOrRequestedVersion.attributes.displayNumber
                    ) ?
                      'NotLatest'
                    : 'Latest'
                  }`}
                  ns='components'
                  values={{
                    latestVersionNumber,
                    signedVersionNumber: signedVersion.attributes.displayNumber,
                  }}
                />
              : <Trans
                  i18nKey={`projectPersonnel.components.ProjectPersonnelDigitalSignaturesInfo.requested${
                    (
                      latestVersionNumber >
                      signedOrRequestedVersion.attributes.displayNumber
                    ) ?
                      'NotLatest'
                    : 'Latest'
                  }`}
                  ns='components'
                  tOptions={{ interpolation: { escapeValue: false } }}
                  values={{
                    latestVersionNumber,
                    requestedAt: asFormattedDateTime({
                      date: requestedVersion.attributes.reviewsRequestedAt,
                    }),
                    requestedVersionNumber:
                      requestedVersion.attributes.displayNumber,
                  }}
                />

            : <Trans
                i18nKey='projectPersonnel.components.ProjectPersonnelDigitalSignaturesInfo.readyForSignature'
                ns='components'
                values={{
                  versionNumber: requestedVersion.attributes.displayNumber,
                }}
              />

          : i18nT('notRequested', {
              interpolation: { escapeValue: false },
              singularRAMSName,
            })
          }
        </span>
      </div>
      {hasNoPersonnel || signedVersion?.id === latestVersionId ?
        <div className='options__toggle options__toggle--btn options__toggle--btn-disabled tw-border-none tw-bg-white'>
          <OutlinedButton
            color='grey'
            disabled={true}
            onClick={() => {}}
            size='sm'
          >
            <SignatureIcon className='-tw-ml-1.5' height={24} width={24} />
            <span className='tw-py-2'>{i18nT('options.toggle')}</span>
            <ChevronDownIcon className='-tw-mr-2' height={24} width={24} />
          </OutlinedButton>
        </div>
      : <Options
          btnToggle={(isOpen) => {
            const Icon = isOpen ? ChevronUpIcon : ChevronDownIcon;

            return (
              <OutlinedButton color='grey' onClick={() => {}} size='sm'>
                <SignatureIcon className='-tw-ml-1.5' height={24} width={24} />
                <span className='tw-py-2'>{i18nT('options.toggle')}</span>
                <Icon className='-tw-mr-2' height={24} width={24} />
              </OutlinedButton>
            );
          }}
        >
          <OptionChoice
            disabled={isReminderOptionDisabled}
            onClick={(setOptionsOpen) => {
              handleSendRemindersOptionClick();
              setOptionsOpen(false);
            }}
          >
            <ConditionalWrapper
              condition={isReminderOptionDisabled}
              wrapper={(children) => (
                <Tooltip
                  placement='top'
                  tooltip={i18nT('options.choices.remind.tooltip')}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                {i18nT('options.choices.remind.button')}
              </a>
            </ConditionalWrapper>
          </OptionChoice>
          <OptionChoice
            disabled={isSignOptionDisabled}
            onClick={(setOptionsOpen) => {
              handleRequestSignaturesOptionClick();
              setOptionsOpen(false);
            }}
          >
            <ConditionalWrapper
              condition={isSignOptionDisabled}
              wrapper={(children) => (
                <Tooltip
                  placement='top'
                  tooltip={i18nT('options.choices.sign.tooltip')}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                {i18nT('options.choices.sign.button')}
              </a>
            </ConditionalWrapper>
          </OptionChoice>
        </Options>
      }
    </div>
  );
}

function Skeleton() {
  return (
    <div className='tw-h-16 tw-w-full tw-animate-pulse tw-rounded-lg tw-bg-grey-100' />
  );
}
